/* Kiosk Token yönetim kartı — Owner Ayarlar sayfasında Sevkiyat Monitörü URL'i üretir. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Monitor, Plus, Copy, Trash2, Loader2, ExternalLink, ScanLine } from 'lucide-react';
import { safeCopy } from '../../../utils/clipboard';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

export default function KioskTokenCard() {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [freshToken, setFreshToken] = useState(null);  // {token, label} — yeni oluşturulan

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/kiosk/tokens`, { headers: hdrs() });
            setTokens(r.data?.items || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        setCreating(true);
        try {
            const r = await axios.post(`${API}/api/kiosk/tokens`,
                { label: newLabel.trim() || 'main' }, { headers: hdrs() });
            setFreshToken({ token: r.data.token, label: r.data.label });
            setNewLabel('');
            toast.success('Token oluşturuldu — kopyalayıp saklayın');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Oluşturulamadı');
        } finally { setCreating(false); }
    };

    const revoke = async (t) => {
        if (!window.confirm(`"${t.label}" tokenini iptal et? Monitör bu URL ile çalışmaz olur.`)) return;
        try {
            await axios.post(`${API}/api/kiosk/tokens/revoke`,
                { label: t.label, created_at: t.created_at }, { headers: hdrs() });
            toast.success('Token iptal edildi');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'İptal edilemedi');
        }
    };

    const copyUrl = (token) => {
        const url = `${window.location.origin}/kiosk/picking?t=${token}`;
        safeCopy(url, 'URL kopyalandı');
    };

    const active = tokens.filter((t) => !t.revoked_at);
    const revoked = tokens.filter((t) => t.revoked_at);

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="kiosk-token-card">
            <div className="flex items-center gap-2">
                <Monitor size={18} className="text-slate-700" />
                <div>
                    <div className="text-sm font-bold text-gray-900">Sevkiyat Monitörü</div>
                    <div className="text-[11px] text-gray-500">
                        Depo monitörüne açtığınız kiosk URL&apos;lerini yönetin — sadece bekleyen sipariş listesi + barcode scan erişimi verir.
                    </div>
                </div>
            </div>

            {freshToken && (
                <div className="bg-amber-50 border border-amber-300 rounded p-3 space-y-2">
                    <div className="text-xs font-bold text-amber-900 flex items-center gap-1">
                        <ScanLine size={13} /> Token 1 kere gösterilir — kopyalayın!
                    </div>
                    <input
                        readOnly
                        value={freshToken.token}
                        onFocus={(e) => e.target.select()}
                        className="w-full font-mono text-[11px] bg-white border border-amber-200 rounded p-2 text-slate-700"
                        data-testid="kiosk-fresh-token-input"
                    />
                    <input
                        readOnly
                        value={`${window.location.origin}/kiosk/picking?t=${freshToken.token}`}
                        onFocus={(e) => e.target.select()}
                        className="w-full font-mono text-[11px] bg-white border border-amber-200 rounded p-2 text-slate-700"
                        data-testid="kiosk-fresh-url-input"
                    />
                    <div className="flex gap-2">
                        <button onClick={() => safeCopy(freshToken.token, 'Token kopyalandı')}
                            className="flex-1 h-8 text-xs font-semibold bg-amber-700 text-white rounded hover:bg-amber-800 flex items-center justify-center gap-1"
                            data-testid="kiosk-copy-token">
                            <Copy size={12} /> Token
                        </button>
                        <button onClick={() => copyUrl(freshToken.token)}
                            className="flex-1 h-8 text-xs font-semibold bg-slate-800 text-white rounded hover:bg-slate-900 flex items-center justify-center gap-1"
                            data-testid="kiosk-copy-url">
                            <ExternalLink size={12} /> Kiosk URL
                        </button>
                        <button onClick={() => setFreshToken(null)}
                            className="h-8 px-3 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                            Kapat
                        </button>
                    </div>
                    <div className="text-[10px] text-amber-800/80 italic">
                        Kopyalama çalışmazsa yukarıdaki alanları tıkla → hepsi seçilir → Ctrl+C yap.
                        Bu pencereyi kapatınca token bir daha görünmez.
                    </div>
                </div>
            )}

            <div className="flex gap-2">
                <input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="Etiket (örn: warehouse-1)"
                    disabled={creating}
                    className="flex-1 h-9 text-sm px-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    data-testid="kiosk-new-label"
                />
                <button onClick={create} disabled={creating}
                    className="h-9 px-3 text-sm font-semibold bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-1"
                    data-testid="kiosk-create-token">
                    {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    Token Oluştur
                </button>
            </div>

            {loading ? (
                <div className="text-center py-3"><Loader2 size={18} className="animate-spin text-gray-400 inline" /></div>
            ) : active.length === 0 ? (
                <div className="text-xs text-gray-400 italic text-center py-2">Aktif token yok</div>
            ) : (
                <div className="space-y-1">
                    {active.map((t) => (
                        <div key={t.label + t.created_at} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5 border border-gray-200">
                            <span className="font-semibold text-gray-800">{t.label}</span>
                            <span className="text-gray-500 text-[10px]">
                                {new Date(t.created_at).toLocaleDateString('tr-TR')}
                            </span>
                            {t.last_used_at && (
                                <span className="text-emerald-700 text-[10px]">
                                    · son kullanım {new Date(t.last_used_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                            <button onClick={() => revoke(t)}
                                className="ml-auto text-rose-600 hover:text-rose-800 p-1"
                                title="İptal et"
                                data-testid={`kiosk-revoke-${t.label}`}>
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {revoked.length > 0 && (
                <details className="text-[11px] text-gray-500">
                    <summary className="cursor-pointer">İptal edilmiş ({revoked.length})</summary>
                    <div className="mt-1 space-y-0.5">
                        {revoked.map((t) => (
                            <div key={t.label + t.created_at} className="text-gray-400 line-through pl-2">
                                {t.label} · {new Date(t.created_at).toLocaleDateString('tr-TR')}
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}
